import React, { useEffect, useState } from 'react';
import { useMobileOptimizedStyles, useIsMobile } from '../../hooks/mobile-hooks';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  InsertDriveFile as InsertDriveFileIcon,
  LocationOn as LocationOnIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import type { ProjectDetailInfo } from '../../types';
import {
  fetchPublicAmenities,
  PublicAmenitiesPayload,
} from '../../services/superPickerPublicApi';
import ProjectMap, { ProjectDetailsTab, SelectedAmenityTypes } from './map/ProjectMap';
import { FULL_VERSION_CAPTION, GATED_DISABLED_SX } from './FullVersionTooltip';

/**
 * Public-side replacement for the agent's project info card. Mirrors the
 * Details / Amenities / Files tab layout from
 * `super-picker/components/siteplan/TopViewingSitePlanDetail.tsx`. The
 * "Project Members" tab from the agent app is intentionally dropped —
 * external/public users have no agent-membership context.
 */

interface ProjectInfoBannerProps {
  project: ProjectDetailInfo;
}

const formatDistance = (meters: number): string => {
  if (meters >= 1000) return `${(meters / 1000).toFixed(1)}km away`;
  return `${Math.round(meters)}m away`;
};

const RADIUS_OPTIONS = [
  { label: '500m', value: 500 },
  { label: '1km', value: 1000 },
  { label: '2km', value: 2000 },
];

const DEFAULT_AMENITY_TYPES: SelectedAmenityTypes = {
  mrtLrt: true,
  schools: true,
  preschools: true,
  supermarkets: true,
};

// Color tokens used both by the checkbox swatches and the per-list dot. The
// agent app uses CSS-var aliases (`--color-danger` etc.); we hard-code the
// resolved hexes here so they line up with the marker colors emitted by
// `ProjectMap` on the public side.
const AMENITY_COLORS = {
  mrtLrt: '#d32f2f',
  schools: '#388e3c',
  preschools: '#7b1fa2',
  supermarkets: '#f57c00',
} as const;

const AMENITY_FILTERS: ReadonlyArray<{
  key: keyof SelectedAmenityTypes;
  label: string;
  color: string;
}> = [
  { key: 'mrtLrt', label: 'MRT/LRT', color: AMENITY_COLORS.mrtLrt },
  { key: 'schools', label: 'Schools', color: AMENITY_COLORS.schools },
  { key: 'preschools', label: 'Preschools', color: AMENITY_COLORS.preschools },
  { key: 'supermarkets', label: 'Supermarkets', color: AMENITY_COLORS.supermarkets },
];

/**
 * Renders one label/value pair inside the Details tab. Styling mirrors the
 * agent's `TopViewingSitePlanDetail.tsx` details panel: caption-sized,
 * `text.secondary` label with `mb: 0.5`, and a `body1` / medium-weight value.
 * Full-width rows (e.g. Address) opt into `fullWidth` to match the agent
 * layout where long-form fields span both columns.
 */
const detailRow = (
  label: string,
  value: React.ReactNode | undefined | null,
  fullWidth = false,
) => {
  if (value == null || value === '') return null;
  return (
    <Grid item xs={12} sm={fullWidth ? 12 : 6}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 0.5 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        fontWeight="medium"
        sx={{
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          minWidth: 0,
        }}
      >
        {value}
      </Typography>
    </Grid>
  );
};

/**
 * Items rendered in the (disabled) Files tab. Same gating as the
 * Factsheet / Brochure row in `ProjectActionButtons` — the underlying
 * documents aren't served in the public app, so the cards are non-clickable
 * placeholders with an inline full-version caption.
 */
const FILE_ITEMS: ReadonlyArray<{ key: string; label: string; icon: React.ReactNode }> = [
  { key: 'factsheet', label: 'Factsheet', icon: <DescriptionIcon /> },
  { key: 'brochure', label: 'Brochure', icon: <InsertDriveFileIcon /> },
];

export const ProjectInfoBanner: React.FC<ProjectInfoBannerProps> = ({
  project,
}) => {
  const mobileOptimizedStyles = useMobileOptimizedStyles();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState<ProjectDetailsTab>('details');
  const [expanded, setExpanded] = useState<boolean>(true);
  /**
   * Inner toggle for the right-hand column on the Details tab only.
   * `image` shows `project.featuredImage`; `map` shows the existing
   * `ProjectMap`. Default to `image` when a featured image is available so
   * the marketing visual is the first thing the user sees.
   */
  const [detailsRightTab, setDetailsRightTab] = useState<'image' | 'map'>('image');

  // Amenities state
  const [amenitiesRadius, setAmenitiesRadius] = useState<number>(1000);
  const [selectedAmenityTypes, setSelectedAmenityTypes] = useState<SelectedAmenityTypes>(DEFAULT_AMENITY_TYPES);
  const [amenitiesLoading, setAmenitiesLoading] = useState(false);
  const [amenitiesData, setAmenitiesData] = useState<PublicAmenitiesPayload | null>(null);
  const [amenitiesError, setAmenitiesError] = useState<string | null>(null);

  const hasMap =
    typeof project.mapLatitude === 'number' &&
    typeof project.mapLongitude === 'number' &&
    Number.isFinite(project.mapLatitude) &&
    Number.isFinite(project.mapLongitude);
  const hasImage =
    typeof project.featuredImage === 'string' && project.featuredImage.trim().length > 0;

  // Keep the inner Details right-column toggle on a tab that has content. If
  // the project has no image we fall back to map; if it has no map (but has
  // an image) we force the image tab.
  useEffect(() => {
    if (detailsRightTab === 'image' && !hasImage && hasMap) {
      setDetailsRightTab('map');
    } else if (detailsRightTab === 'map' && !hasMap && hasImage) {
      setDetailsRightTab('image');
    }
  }, [detailsRightTab, hasImage, hasMap]);

  // Fetch amenities when entering the amenities tab or radius changes.
  useEffect(() => {
    if (tab !== 'amenities' || !project.id || !hasMap) return;
    let cancelled = false;
    setAmenitiesLoading(true);
    setAmenitiesError(null);
    fetchPublicAmenities(project.id, amenitiesRadius)
      .then((data) => {
        if (!cancelled) setAmenitiesData(data);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[ProjectInfoBanner] amenities fetch failed:', err);
        setAmenitiesError('Failed to load amenities for this radius.');
        setAmenitiesData(null);
      })
      .finally(() => { if (!cancelled) setAmenitiesLoading(false); });
    return () => { cancelled = true; };
  }, [tab, project.id, amenitiesRadius, hasMap]);

  const renderDetails = () => (
    // Spacing + structure mirror the agent's Details tab in
    // `TopViewingSitePlanDetail.tsx`. The container sizes naturally; the
    // right-hand Image / Map box stretches to match its height (see the
    // `flex: 1` wrapper below).
    <Grid container spacing={2}>
      {/* Project name — full-width, matches the agent's first row. */}
      {detailRow('Name', project.name, true)}
      {detailRow('Developer', project.developer)}
      {detailRow('Tenure', project.tenure)}
      {detailRow('TOP', (() => {
        const raw = project.completedDate || project.topYear || '';
        const match = raw.match(/\d{4}/);
        return match ? match[0] : null;
      })())}
      {detailRow('District', project.district)}
      {detailRow('Region', project.region)}
      {detailRow('Area', project.area)}
      {detailRow('Total Units', project.totalUnits ? String(project.totalUnits) : null)}
      {/* Address always spans the full row, matching the agent's xs={12}. */}
      {detailRow('Address', project.address, true)}
    </Grid>
  );

  const renderAmenities = () => (
    <Box
      sx={{
        maxHeight: { xs: 480, md: 'calc(100vh - 320px)' },
        overflowY: 'auto',
        pr: 1,
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
          Search Radius
        </Typography>
        <ToggleButtonGroup
          value={amenitiesRadius}
          exclusive
          onChange={(_, v) => { if (v != null) setAmenitiesRadius(v); }}
          size="small"
        >
          {RADIUS_OPTIONS.map((opt) => (
            <ToggleButton key={opt.value} value={opt.value}>{opt.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
          Show Amenities
        </Typography>
        <FormGroup row>
          {AMENITY_FILTERS.map((f) => (
            <FormControlLabel
              key={f.key}
              control={
                <Checkbox
                  size="small"
                  checked={selectedAmenityTypes[f.key]}
                  onChange={(e) =>
                    setSelectedAmenityTypes((p) => ({ ...p, [f.key]: e.target.checked }))
                  }
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      backgroundColor: f.color,
                      borderRadius: '2px',
                      border: '1px solid rgba(var(--color-shadow-rgb), 0.1)',
                    }}
                  />
                  <Typography variant="body2">{f.label}</Typography>
                </Box>
              }
            />
          ))}
        </FormGroup>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {amenitiesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : amenitiesError ? (
        <Typography variant="body2" sx={{ color: 'error.main' }}>{amenitiesError}</Typography>
      ) : amenitiesData ? (
        <Box>
          {selectedAmenityTypes.mrtLrt && (
            <AmenityList
              title="MRT/LRT"
              color={AMENITY_COLORS.mrtLrt}
              items={amenitiesData.amenities.mrtLrt
                .filter((s) => s.name && s.name.trim() !== '' && s.name.trim() !== '-')
                .map((s) => ({ key: `mrt-${s.id}`, name: s.name, meta: '', distance: s.distance }))}
            />
          )}
          {selectedAmenityTypes.schools && (
            <AmenityList
              title="Schools"
              color={AMENITY_COLORS.schools}
              items={amenitiesData.amenities.schools
                .filter((s) => s.name && s.name.trim() !== '' && s.name.trim() !== '-')
                .map((s) => ({
                  key: `school-${s.id}`,
                  name: s.name,
                  meta: [s.type, s.category].filter(Boolean).join(' · '),
                  distance: s.distance,
                }))}
            />
          )}
          {selectedAmenityTypes.preschools && (
            <AmenityList
              title="Preschools"
              color={AMENITY_COLORS.preschools}
              items={amenitiesData.amenities.preschools
                .filter((s) => s.name && s.name.trim() !== '' && s.name.trim() !== '-')
                .map((p) => ({
                  key: `preschool-${p.id}`,
                  name: p.name,
                  meta: [p.type, p.category].filter(Boolean).join(' · '),
                  distance: p.distance,
                }))}
            />
          )}
          {selectedAmenityTypes.supermarkets && (
            <AmenityList
              title="Supermarkets"
              color={AMENITY_COLORS.supermarkets}
              items={amenitiesData.amenities.supermarkets
                .filter((s) => s.name && s.name.trim() !== '' && s.name.trim() !== '-')
                .map((m) => ({
                  key: `supermart-${m.id}`,
                  name: m.name,
                  meta: m.address ?? '',
                  distance: m.distance,
                }))}
            />
          )}
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No amenities to show.
        </Typography>
      )}
    </Box>
  );

  // Files tab (agent parity). The brochure / factsheet aren't available in
  // the public ("lite") app, so the cards render disabled + non-clickable
  // with an inline caption and no backend query is made.
  const renderFiles = () => (
    <Grid container spacing={2}>
      {FILE_ITEMS.map((file) => (
        <Grid item xs={12} sm={6} key={file.key}>
          <DisabledFileCard icon={file.icon} title={file.label} />
        </Grid>
      ))}
    </Grid>
  );

  // Layout decision (mirrors agent): details/amenities render a 2-column
  // layout with media (image/map) on the right when something is available
  // to show. On the amenities tab only the map can appear; on details the
  // right column can also surface `project.featuredImage`.
  const hasRightColumn =
    tab === 'amenities'
      ? hasMap
      : tab === 'details'
        ? hasMap || hasImage
        : false;
  const leftColSpan = !hasRightColumn ? 12 : 6;
  const showDetailsMediaToggle = tab === 'details' && hasImage && hasMap;

  const detailsMediaTabs = showDetailsMediaToggle ? (
    <Tabs
      value={detailsRightTab}
      onChange={(_, v) => setDetailsRightTab(v as 'image' | 'map')}
      variant={isMobile ? 'fullWidth' : 'standard'}
      sx={{
        minHeight: 36,
        '& .MuiTab-root': {
          minHeight: 36,
          py: 0.5,
          color: 'rgba(var(--color-text-rgb), 0.7)',
          '&.Mui-selected': { color: 'var(--color-accent)' },
        },
        '& .MuiTabs-indicator': { bgcolor: 'var(--color-accent)' },
      }}
    >
      <Tab label="Image" value="image" />
      <Tab label="Map View" value="map" />
    </Tabs>
  ) : null;

  return (
    <Card sx={{ mb: 2, bgcolor: 'var(--color-surface)', ...mobileOptimizedStyles }}>
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
        sx={{
          bgcolor: 'var(--color-surface)',
          boxShadow: 'none',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'var(--color-text-strong)' }} />}
          sx={{
            px: { xs: 1.5, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            bgcolor: 'var(--color-surface)',
            '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.05)' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 0.5, sm: 1 },
              width: '100%',
              minWidth: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, maxWidth: '100%' }}>
              <LocationOnIcon sx={{ color: 'var(--color-accent)', flexShrink: 0 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'var(--color-text-strong)',
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {project.name || 'Project Information'}
              </Typography>
            </Box>
            {!expanded && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1.5, sm: 2 },
                  ml: { xs: 4, sm: 0 },
                  flexWrap: 'wrap',
                }}
              >
                {(() => {
                  const raw = project.completedDate || project.topYear || '';
                  const match = raw.match(/\d{4}/);
                  const year = match ? match[0] : '';
                  if (!year) return null;
                  return (
                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', whiteSpace: 'nowrap' }}>
                      TOP: <strong>{year}</strong>
                    </Typography>
                  );
                })()}
                {project.district && (
                  <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', whiteSpace: 'nowrap' }}>
                    District: <strong>{project.district}</strong>
                  </Typography>
                )}
                {project.region && (
                  <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', whiteSpace: 'nowrap' }}>
                    Region: <strong>{project.region}</strong>
                  </Typography>
                )}
                {project.totalUnits != null && (
                  <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', whiteSpace: 'nowrap' }}>
                    Units: <strong>{project.totalUnits}</strong>
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0, bgcolor: 'var(--color-surface)' }}>
          <CardContent sx={{ pt: 1, overflow: 'hidden' }}>
            {/* Primary tabs only — on mobile the Image / Map toggle lives
                below the details content so the tab row doesn't force
                horizontal scrolling (agent parity). */}
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                gap: 2,
                borderBottom: '1px solid rgba(var(--color-text-rgb), 0.1)',
              }}
            >
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v as ProjectDetailsTab)}
                variant={isMobile ? 'fullWidth' : 'standard'}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  '& .MuiTab-root': {
                    color: 'rgba(var(--color-text-rgb), 0.7)',
                    '&.Mui-selected': { color: 'var(--color-accent)' },
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    px: { xs: 1, sm: 2 },
                  },
                  '& .MuiTabs-indicator': { bgcolor: 'var(--color-accent)' },
                }}
              >
                <Tab label="Details" value="details" />
                <Tab label="Amenities" value="amenities" />
                <Tab label="Files" value="files" />
              </Tabs>
              {/* Desktop: Image / Map toggle inline with primary tabs. */}
              {!isMobile && detailsMediaTabs}
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={leftColSpan}>
                {tab === 'details' && renderDetails()}
                {tab === 'amenities' && renderAmenities()}
                {tab === 'files' && renderFiles()}
                {/* Mobile: Image / Map toggle below details, above media pane. */}
                {isMobile && tab === 'details' && detailsMediaTabs && (
                  <Box sx={{ mt: 2, mb: 1 }}>{detailsMediaTabs}</Box>
                )}
              </Grid>

              {hasRightColumn && (
                // `display: flex` lets the inner media box claim the full
                // height of this Grid item (which MUI already stretches to
                // match its sibling), so the Image / Map pane scales to the
                // bottom of the Details content instead of being pinned to
                // a fixed 420px.
                <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                  <Box
                    data-pdf-capture={tab === 'amenities' ? 'amenities-map' : undefined}
                    sx={{
                      // xs/sm: fixed comfortable height (no sibling beside
                      // it to match). md+: stretch to match the Details
                      // column's natural height via `flex: 1`. A small
                      // `minHeight` keeps the box usable for projects with
                      // very sparse detail data.
                      width: '100%',
                      height: { xs: 320, md: 'auto' },
                      flex: { xs: 'none', md: 1 },
                      minHeight: { md: 320 },
                      // Cap the media pane on `md+` so a very long
                      // Details column (many populated rows) doesn't
                      // stretch the featured image / map to an awkward
                      // tall aspect ratio. The Details column keeps its
                      // natural height; only the right pane caps out.
                      maxHeight: { md: 520 },
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid rgba(var(--color-text-rgb), 0.12)',
                      bgcolor: 'rgba(var(--color-text-rgb), 0.04)',
                    }}
                  >
                    {tab === 'details' && detailsRightTab === 'image' && hasImage ? (
                      <Box
                        component="img"
                        src={project.featuredImage as string}
                        alt={project.name}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    ) : hasMap ? (
                      <ProjectMap
                        latitude={project.mapLatitude!}
                        longitude={project.mapLongitude!}
                        tab={tab}
                        amenitiesData={amenitiesData}
                        radius={amenitiesRadius}
                        selectedAmenityTypes={selectedAmenityTypes}
                      />
                    ) : null}
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </AccordionDetails>
      </Accordion>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface AmenityListItem {
  key: string;
  name: string;
  meta: string;
  distance: number;
}
const AmenityList: React.FC<{ title: string; color: string; items: AmenityListItem[] }> = ({ title, color, items }) => {
  if (items.length === 0) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title} ({items.length})
        </Typography>
      </Box>
      <Box>
        {items.map((it) => (
          <Card key={it.key} sx={{ mb: 1 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="body2" fontWeight="medium">
                {it.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {[it.meta, formatDistance(it.distance)].filter(Boolean).join(' · ')}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

/**
 * Non-interactive Files-tab card. Styled like the agent's file rows but
 * always disabled (greyed out, no click target) — the actual documents are
 * gated behind the full version with an inline caption below the title.
 */
const DisabledFileCard: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <Card
    variant="outlined"
    sx={{ cursor: 'default', width: '100%', ...GATED_DISABLED_SX }}
  >
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {FULL_VERSION_CAPTION}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

export default ProjectInfoBanner;
